import { type FormEvent, useState } from 'react'

import {
  Button,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui'
import type { AccountDetails } from '@/modules/projects/components/Workbench/accounts'
import { FilingCard } from './FilingCard'
import { NumbersField } from './NumbersField'
import { TypeField } from './TypeField'
import type { FilingNote } from './types'

const BLANK_ACCOUNT: AccountDetails = {
  numbers: [''],
  accountType: null,
  owner: null,
  institution: null,
}

const typedNumbers = (details: AccountDetails) =>
  details.numbers
    .map((number) => number.trim())
    .filter((number) => number.length > 0)

const messageOf = (error: unknown) =>
  error instanceof Error && error.message
    ? error.message
    : 'Could not add the account.'

export interface AddAccountFormProps {
  filing: FilingNote | null
  onAdd: (details: AccountDetails) => Promise<void>
  onClose: () => void
}

// Lives inside the dialog content, so closing the dialog throws the draft away with it.
export const AddAccountForm = ({
  filing,
  onAdd,
  onClose,
}: AddAccountFormProps) => {
  const [draft, setDraft] = useState(BLANK_ACCOUNT)
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const numbers = typedNumbers(draft)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (numbers.length === 0 || saving) return
    setSaving(true)
    setProblem(null)
    try {
      await onAdd({ ...draft, numbers })
      onClose()
    } catch (error) {
      setProblem(messageOf(error))
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <DialogHeader>
        <DialogTitle>Add an account</DialogTitle>
        <DialogDescription>
          {filing
            ? 'The number was never read off these pages. The statement you have open moves under it as soon as it exists.'
            : 'It starts with no statements. File pages into it from any month on the map.'}
        </DialogDescription>
      </DialogHeader>

      {filing ? <FilingCard filing={filing} /> : null}

      <NumbersField details={draft} onChange={setDraft} />
      <TypeField
        accountType={draft.accountType}
        onPick={(accountType) => setDraft({ ...draft, accountType })}
      />
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="add-account-institution">Institution</Label>
          <Input
            id="add-account-institution"
            data-account-institution-input
            value={draft.institution ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, institution: event.target.value || null })
            }
            placeholder="As printed on the letterhead"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="add-account-owner">Owner</Label>
          <Input
            id="add-account-owner"
            data-account-owner-input
            value={draft.owner ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, owner: event.target.value || null })
            }
            placeholder="Name on the statement"
          />
        </div>
      </div>

      {problem ? (
        <p data-add-account-problem className="text-sm text-red-600">
          {problem}
        </p>
      ) : null}

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" theme="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={numbers.length === 0 || saving}>
          {filing ? 'Add and file it here' : 'Add account'}
        </Button>
      </div>
    </form>
  )
}
